/* Jubi Style Finder — quiz flow, profile scoring, email gate, result assembly.
   Questions/answers live in the section markup as data attributes; this file
   handles state, navigation, and Klaviyo capture. No dependencies.

   Klaviyo: on email submit we (1) subscribe the address to the list set in the
   section settings and (2) fire a "Completed Style Quiz" event carrying the
   answers, so flows can trigger on the metric without touching list logic.
   Both calls are fire-and-forget (keepalive) — results never block on them. */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var STORAGE_KEY = 'jubi-style-finder-subscribed';

  function initStyleFinder(root) {
    if (root.dataset.jsfInit) return;
    root.dataset.jsfInit = 'true';

    var stages = {
      intro: root.querySelector('[data-jsf-intro]'),
      quiz: root.querySelector('[data-jsf-quiz]'),
      email: root.querySelector('[data-jsf-email]'),
      result: root.querySelector('[data-jsf-result]')
    };
    var questions = Array.prototype.slice.call(root.querySelectorAll('[data-jsf-question]'));
    var stepLabel = root.querySelector('[data-jsf-step]');
    var progressFill = root.querySelector('[data-jsf-progress]');
    var linksList = root.querySelector('[data-jsf-links]');
    var tipEl = root.querySelector('[data-jsf-tip]');
    var ctaEl = root.querySelector('[data-jsf-cta]');
    var emailForm = root.querySelector('[data-jsf-email-form]');
    var emailInput = emailForm ? emailForm.querySelector('input[type="email"]') : null;
    var emailButton = emailForm ? emailForm.querySelector('button[type="submit"]') : null;
    var emailError = root.querySelector('[data-jsf-email-error]');

    var answers = {};
    var current = 0;

    function showStage(name) {
      Object.keys(stages).forEach(function (key) {
        if (stages[key]) stages[key].classList.toggle('is-active', key === name);
      });
    }

    function showQuestion(index) {
      current = index;
      questions.forEach(function (q, i) {
        q.classList.toggle('is-active', i === index);
      });
      if (stepLabel) stepLabel.textContent = String(index + 1);
      if (progressFill) progressFill.style.width = ((index + 1) / questions.length) * 100 + '%';
      var title = questions[index].querySelector('.jsf__question-title');
      if (title) title.focus();
    }

    function parseLinks(el) {
      try {
        return JSON.parse(el.getAttribute('data-links') || '[]');
      } catch (err) {
        return [];
      }
    }

    function optionName(el) {
      var name = el && el.querySelector('.jsf__option-name');
      return name ? name.textContent.trim() : '';
    }

    function computeResult() {
      var paletteClass = answers.palette ? answers.palette.getAttribute('data-class') : 'tonal';
      var patternKey = answers.pattern ? answers.pattern.getAttribute('data-value') : 'solid';
      var profileKey = patternKey + '-' + paletteClass;
      var profileEl = root.querySelector('[data-profile="' + profileKey + '"]');

      var links = [];
      var seen = {};
      ['palette', 'pattern', 'texture', 'room'].forEach(function (key) {
        if (!answers[key]) return;
        parseLinks(answers[key]).forEach(function (link) {
          if (!link.url || seen[link.url]) return;
          seen[link.url] = true;
          links.push(link);
        });
      });

      var nameEl = profileEl ? profileEl.querySelector('.jsf__profile-name') : null;
      var blurbEl = profileEl ? profileEl.querySelector('.jsf__profile-blurb') : null;

      var rugs = [];
      if (profileEl) {
        profileEl.querySelectorAll('.jsf__product').forEach(function (card) {
          var img = card.querySelector('img');
          var title = card.querySelector('.jsf__product-title');
          var price = card.querySelector('.jsf__product-price');
          var src = img ? (img.getAttribute('src') || '') : '';
          if (src.indexOf('//') === 0) src = 'https:' + src;
          rugs.push({
            title: title ? title.textContent.trim() : '',
            price: price ? price.textContent.trim() : '',
            url: card.getAttribute('href') || '',
            image: src
          });
        });
      }

      return {
        rugs: rugs,
        key: profileKey,
        el: profileEl,
        name: nameEl ? nameEl.textContent.trim() : '',
        blurb: blurbEl ? blurbEl.textContent.trim() : '',
        links: links,
        tip: answers.room ? (answers.room.getAttribute('data-tip') || '') : '',
        ctaLabel: answers.path ? (answers.path.getAttribute('data-cta-label') || '') : '',
        ctaUrl: answers.path ? (answers.path.getAttribute('data-cta-url') || '') : ''
      };
    }

    function renderResult() {
      var result = computeResult();

      root.querySelectorAll('[data-profile]').forEach(function (profile) {
        profile.hidden = profile !== result.el;
      });

      if (linksList) {
        linksList.innerHTML = '';
        result.links.forEach(function (link) {
          var li = document.createElement('li');
          var a = document.createElement('a');
          a.className = 'jsf__match-link';
          a.href = link.url;
          a.textContent = link.label + ' →';
          li.appendChild(a);
          linksList.appendChild(li);
        });
      }

      if (tipEl) tipEl.textContent = result.tip;

      if (ctaEl && result.ctaUrl) {
        ctaEl.textContent = result.ctaLabel || ctaEl.textContent;
        ctaEl.href = result.ctaUrl;
      }

      showStage('result');
      var name = root.querySelector('[data-profile]:not([hidden]) .jsf__profile-name');
      if (name) name.focus();
    }

    function alreadySubscribed() {
      try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
      } catch (err) {
        return false;
      }
    }

    function finishQuiz() {
      if (root.dataset.emailGate === 'true' && stages.email && !alreadySubscribed()) {
        showStage('email');
        var heading = stages.email.querySelector('.jsf__heading');
        if (heading) heading.focus();
      } else {
        renderResult();
      }
    }

    function sendToKlaviyo(email) {
      var companyId = root.dataset.klaviyoCompany;
      if (!companyId || !window.fetch) return;

      var source = 'direct';
      try {
        source = new URLSearchParams(window.location.search).get('src') || 'direct';
      } catch (err) { /* older browsers — keep 'direct' */ }

      var result = computeResult();
      var listId = root.dataset.klaviyoList;
      var properties = {
        'Quiz Source': source,
        'Style Profile': result.name || result.key,
        'Quiz Palette': optionName(answers.palette),
        'Quiz Pattern': optionName(answers.pattern),
        'Quiz Texture': optionName(answers.texture),
        'Quiz Room': optionName(answers.room),
        'Quiz Path': optionName(answers.path)
      };

      var subscription = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Style Finder Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, properties: properties }
              }
            }
          }
        }
      };
      if (listId) {
        subscription.data.relationships = {
          list: { data: { type: 'list', id: listId } }
        };
      }

      var event = {
        data: {
          type: 'event',
          attributes: {
            properties: {
              'Quiz Source': source,
              'Style Profile': result.name || result.key,
              'Profile Blurb': result.blurb,
              'Quiz Palette': properties['Quiz Palette'],
              'Quiz Pattern': properties['Quiz Pattern'],
              'Quiz Texture': properties['Quiz Texture'],
              'Quiz Room': properties['Quiz Room'],
              'Quiz Path': properties['Quiz Path'],
              'Size Tip': result.tip,
              'Matches': result.links,
              'Featured Rugs': result.rugs,
              'CTA Label': result.ctaLabel,
              'CTA URL': result.ctaUrl
            },
            metric: {
              data: { type: 'metric', attributes: { name: 'Completed Style Quiz' } }
            },
            profile: {
              data: { type: 'profile', attributes: { email: email } }
            }
          }
        }
      };

      [
        { path: 'subscriptions', body: subscription },
        { path: 'events', body: event }
      ].forEach(function (call) {
        fetch('https://a.klaviyo.com/client/' + call.path + '/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(call.body)
        }).catch(function () {});
      });
    }

    function submitEmail(e) {
      e.preventDefault();
      var email = emailInput ? emailInput.value.trim() : '';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (emailError) emailError.hidden = false;
        if (emailInput) emailInput.focus();
        return;
      }
      if (emailError) emailError.hidden = true;
      if (emailButton) emailButton.disabled = true;

      sendToKlaviyo(email);
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (err) { /* private browsing — results still show */ }

      renderResult();
      if (emailButton) emailButton.disabled = false;
    }

    function reset() {
      answers = {};
      root.querySelectorAll('.jsf__option.is-selected').forEach(function (btn) {
        btn.classList.remove('is-selected');
      });
      showStage('quiz');
      showQuestion(0);
    }

    var startBtn = root.querySelector('[data-jsf-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function () {
        showStage('quiz');
        showQuestion(0);
      });
    }

    questions.forEach(function (question) {
      question.querySelectorAll('.jsf__option').forEach(function (option) {
        option.addEventListener('click', function () {
          var key = question.getAttribute('data-key');
          answers[key] = option;
          question.querySelectorAll('.jsf__option').forEach(function (btn) {
            btn.classList.toggle('is-selected', btn === option);
          });
          var next = questions.indexOf(question) + 1;
          if (next < questions.length) {
            showQuestion(next);
          } else {
            finishQuiz();
          }
        });
      });
    });

    root.querySelectorAll('[data-jsf-back]').forEach(function (back) {
      back.addEventListener('click', function () {
        if (current > 0) {
          showQuestion(current - 1);
        } else {
          showStage('intro');
        }
      });
    });

    var emailBack = root.querySelector('[data-jsf-email-back]');
    if (emailBack) {
      emailBack.addEventListener('click', function () {
        showStage('quiz');
        showQuestion(questions.length - 1);
      });
    }

    if (emailForm) emailForm.addEventListener('submit', submitEmail);

    var skip = root.querySelector('[data-jsf-skip]');
    if (skip) skip.addEventListener('click', renderResult);

    var retake = root.querySelector('[data-jsf-retake]');
    if (retake) retake.addEventListener('click', reset);
  }

  function initAll() {
    document.querySelectorAll('[data-style-finder]').forEach(initStyleFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var el = event.target.querySelector('[data-style-finder]');
    if (el) initStyleFinder(el);
  });
})();
